import { Injectable, signal } from '@angular/core';
import {
  AuthChangeEvent,
  Session,
  SupabaseClient,
  createClient,
  processLock
} from '@supabase/supabase-js';
import { environment } from '../../../environments/environment';
import { assertValidEnvironment } from '../../../environments/environment.model';
import { Database } from '../supabase/database.types';
import { stagingTrace, stagingTraceError } from '../diagnostics/staging-diagnostics';
import { isAppLocale, type AppLocale } from '../i18n/i18n.types';

export interface SignUpResult {
  requiresEmailConfirmation: boolean;
}

export interface UserProfile {
  username: string;
  locale: AppLocale;
}

export type PasswordRecoveryStatus = 'idle' | 'checking' | 'ready' | 'invalid';

@Injectable({ providedIn: 'root' })
export class SupabaseService {
  private readonly sb: SupabaseClient<Database>;
  private readonly initialSession: Promise<Session | null>;
  private readonly profileUsernameState = signal<string | null>(null);
  private readonly passwordRecoveryStatusState = signal<PasswordRecoveryStatus>('idle');
  private readonly recoveryCallbackKind: 'none' | 'recovery' | 'error';

  readonly passwordRecoveryStatus = this.passwordRecoveryStatusState.asReadonly();

  session: Session | null = null;

  constructor() {
    assertValidEnvironment(environment);

    this.recoveryCallbackKind = this.detectRecoveryCallbackKind();
    if (this.isPasswordRecoveryRoute()) {
      this.passwordRecoveryStatusState.set('checking');
    }

    // Auth JS 2.75 + zone.js may leave navigator.locks acquisition pending.
    // Limit this temporary single-tab mutex workaround to staging only:
    // it prevents Web Locks/Zone interoperability errors without disabling
    // in-tab serialization. Remove after upgrading auth-js to >= 2.107.
    const stagingProcessLock = environment.name === 'staging';
    stagingTrace('AUTH', 'client:init', {
      lockStrategy: stagingProcessLock ? 'processLock' : 'sdk-default'
    });

    this.sb = createClient<Database>(environment.supabaseUrl, environment.supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        ...(stagingProcessLock ? { lock: processLock } : {})
      }
    });

    this.sb.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      stagingTrace('AUTH', 'state:changed', { event, hasSession: session !== null });
      this.session = session;

      if (event === 'PASSWORD_RECOVERY') {
        this.passwordRecoveryStatusState.set(session ? 'ready' : 'invalid');
        this.clearRecoveryCallbackUrl();
      }
    });

    const sessionStartedAt = performance.now();
    stagingTrace('AUTH', 'session:start');
    this.initialSession = this.sb.auth.getSession().then(({ data, error }) => {
      stagingTrace('AUTH', 'session:resolved', {
        hasSession: data.session !== null,
        hasError: error !== null,
        durationMs: Math.round(performance.now() - sessionStartedAt)
      });
      if (error) {
        this.session = null;
        if (this.isPasswordRecoveryRoute()) {
          this.passwordRecoveryStatusState.set('invalid');
          this.clearRecoveryCallbackUrl();
        }
        return null;
      }

      this.session = data.session;

      if (
        this.isPasswordRecoveryRoute() &&
        this.passwordRecoveryStatusState() === 'checking'
      ) {
        const isValidRecovery =
          this.recoveryCallbackKind === 'recovery' &&
          data.session !== null;

        this.passwordRecoveryStatusState.set(isValidRecovery ? 'ready' : 'invalid');
        this.clearRecoveryCallbackUrl();
      }

      return data.session;
    }).catch((error: unknown) => {
      stagingTraceError('AUTH', 'session:rejected', error, {
        durationMs: Math.round(performance.now() - sessionStartedAt)
      });
      this.session = null;
      if (this.isPasswordRecoveryRoute()) {
        this.passwordRecoveryStatusState.set('invalid');
        this.clearRecoveryCallbackUrl();
      }
      return null;
    });
  }

  get client(): SupabaseClient<Database> {
    return this.sb;
  }

  get isLoggedIn(): boolean {
    return this.session !== null;
  }

  get userId(): string | null {
    return this.session?.user.id ?? null;
  }

  get userEmail(): string | null {
    return this.session?.user.email ?? null;
  }

  get userName(): string {
    return this.profileUsernameState() ?? '';
  }

  async ensureSession(): Promise<Session | null> {
    await this.initialSession;
    return this.session;
  }

  async signInWithPassword(email: string, password: string): Promise<Session> {
    const { data, error } = await this.sb.auth.signInWithPassword({ email, password });
    if (error || !data.session) {
      throw error ?? new Error('Session Supabase absente après la connexion.');
    }

    this.session = data.session;
    return data.session;
  }

  async requestPasswordReset(email: string): Promise<void> {
    const { error } = await this.sb.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: environment.passwordRecoveryRedirectUrl
    });

    if (error) throw error;
  }

  async initializePasswordRecovery(): Promise<boolean> {
    await this.initialSession;

    if (!this.isPasswordRecoveryRoute()) return false;

    if (this.passwordRecoveryStatusState() === 'checking') {
      const valid =
        this.recoveryCallbackKind === 'recovery' &&
        this.session !== null;

      this.passwordRecoveryStatusState.set(valid ? 'ready' : 'invalid');
      this.clearRecoveryCallbackUrl();
    }

    return this.passwordRecoveryStatusState() === 'ready';
  }

  async updatePasswordFromRecovery(password: string): Promise<void> {
    if (
      this.passwordRecoveryStatusState() !== 'ready' ||
      !this.session
    ) {
      throw new Error('Invalid password recovery session.');
    }

    const { data, error } = await this.sb.auth.updateUser({ password });
    if (error || !data.user) {
      throw error ?? new Error('Utilisateur Supabase absent après la mise à jour du mot de passe.');
    }

    this.passwordRecoveryStatusState.set('idle');
  }

  async signUpWithPassword(
    username: string,
    email: string,
    password: string,
    locale: AppLocale = 'fr'
  ): Promise<SignUpResult> {
    const { data, error } = await this.sb.auth.signUp({
      email,
      password,
      options: {
        data: {
          username,
          locale
        }
      }
    });

    if (error || !data.user) {
      throw error ?? new Error('Utilisateur Supabase absent après l’inscription.');
    }

    this.session = data.session;
    if (data.session) {
      await this.loadCurrentProfile();
    }
    return { requiresEmailConfirmation: data.session === null };
  }

  async loadCurrentProfile(): Promise<UserProfile | null> {
    const userId = this.userId;
    if (!userId) {
      this.profileUsernameState.set(null);
      return null;
    }

    const { data, error } = await this.sb
      .from('profiles')
      .select('username, locale')
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;

    if (!data) {
      await this.signOut();
      return null;
    }

    const username = data.username.trim();
    if (username.length < 2 || username.length > 80) {
      throw new Error('Invalid profile username.');
    }

    const locale = isAppLocale(data.locale) ? data.locale : 'fr';
    this.profileUsernameState.set(username);
    return { username, locale };
  }

  async getProfileLocale(): Promise<string | null> {
    const profile = await this.loadCurrentProfile();
    return profile?.locale ?? null;
  }

  async updateProfileLocale(locale: AppLocale): Promise<void> {
    const userId = this.userId;
    if (!userId) throw new Error('Authenticated user required to update locale.');

    const { error } = await this.sb
      .from('profiles')
      .update({ locale })
      .eq('id', userId);

    if (error) throw error;
  }

  async updateProfileUsername(value: string): Promise<string> {
    const userId = this.userId;
    if (!userId) throw new Error('Authenticated user required to update username.');

    const username = value.trim();
    if (username.length < 2 || username.length > 80) {
      throw new Error('Invalid profile username.');
    }

    const { data, error } = await this.sb
      .from('profiles')
      .update({ username })
      .eq('id', userId)
      .select('username')
      .single();

    if (error) throw error;

    this.profileUsernameState.set(data.username);
    return data.username;
  }

  async signOut(): Promise<void> {
    const { error } = await this.sb.auth.signOut();
    this.session = null;
    this.profileUsernameState.set(null);
    this.passwordRecoveryStatusState.set('idle');
    if (error) throw error;
  }

  async clearLocalSessionAfterAccountDeletion(): Promise<void> {
    try {
      await this.sb.auth.signOut({ scope: 'local' });
    } finally {
      this.session = null;
      this.profileUsernameState.set(null);
      this.passwordRecoveryStatusState.set('idle');
    }
  }

  private isPasswordRecoveryRoute(): boolean {
    return typeof window !== 'undefined' && window.location.pathname === '/reset-password';
  }

  private detectRecoveryCallbackKind(): 'none' | 'recovery' | 'error' {
    if (typeof window === 'undefined' || !this.isPasswordRecoveryRoute()) return 'none';

    const query = new URLSearchParams(window.location.search);
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));

    if (
      query.has('error') ||
      query.has('error_code') ||
      fragment.has('error') ||
      fragment.has('error_code')
    ) {
      return 'error';
    }

    if (query.get('type') === 'recovery' || fragment.get('type') === 'recovery') {
      return 'recovery';
    }

    return 'none';
  }

  private clearRecoveryCallbackUrl(): void {
    if (
      typeof window === 'undefined' ||
      !this.isPasswordRecoveryRoute() ||
      this.recoveryCallbackKind === 'none'
    ) {
      return;
    }

    window.history.replaceState(window.history.state, '', '/reset-password');
  }
}
